/**
 * Converts a parameters object into a URL-encoded query string.
 *
 * This function iterates through the properties of the given FAQ parameters object,
 * skipping any keys where the value is undefined or an empty string.
 * For properties that are objects (e.g., nested filters),
 * it further iterates over their key-value pairs and appends them using bracket notation.
 * The resulting string is properly encoded and can be appended to a URL for HTTP requests.
 */

import { FaqRequestParams } from '../types';

export const encodeObjectToQueryString = (
  queryObj: FaqRequestParams
): string => {
  // Check if defined or queryObj is not an object and return an empty string immediately
  if (!queryObj || typeof queryObj !== 'object') return '';

  const params = new URLSearchParams();

  Object.entries(queryObj).forEach(([key, value]) => {
    // Skip undefined or empty strings
    if (value === undefined || value === '') return;

    // If the value is a string, append it directly.
    if (typeof value === 'string') {
      params.append(key, value);
    }

    // Otherwise, if it's an object (and not an array), iterate its entries.
    else if (typeof value === 'object' && !Array.isArray(value)) {
      Object.entries(value).forEach(([nestedKey, nestedValue]) => {
        // Skip undefined or empty strings at the nested level
        if (nestedValue === undefined || nestedValue === '') return;

        // If the value is a string, append it directly.
        if (typeof nestedValue === 'string') {
          params.append(`${key}[${nestedKey}]`, nestedValue);
        }
      });
    }
  });

  const queryString = params.toString();

  return queryString;
};
